import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const BottomTabNavigation = ({ 
  className = '',
  onTabChange = () => {},
  notificationCounts = {}
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'Home',
      tooltip: 'Overview and quick actions'
    },
    {
      label: 'Contacts',
      path: '/contact-list',
      icon: 'Users',
      tooltip: 'Manage your contacts'
    },
    {
      label: 'Messages',
      path: '/message-templates',
      icon: 'MessageSquare',
      tooltip: 'Templates and history'
    },
    {
      label: 'Calendar',
      path: '/birthday-calendar',
      icon: 'Calendar',
      tooltip: 'Birthday reminders',
      badge: notificationCounts?.birthdays
    }
  ];

  const handleTabClick = (item) => {
    navigate(item?.path);
    onTabChange(item);
  };

  const isActiveTab = (path) => {
    if (path === '/dashboard') {
      return location?.pathname === '/dashboard';
    }
    if (path === '/contact-list') {
      return ['/contact-list', '/contact-details', '/add-edit-contact']?.includes(location?.pathname);
    }
    if (path === '/message-templates') {
      return ['/message-templates', '/message-history', '/random-contact']?.includes(location?.pathname);
    }
    if (path === '/birthday-calendar') {
      return location?.pathname === '/birthday-calendar';
    }
    return false;
  };

  return (
    <>
      {/* Mobile Bottom Navigation */}
      <nav className={`lg:hidden fixed bottom-0 left-0 right-0 z-100 bg-surface border-t border-border shadow-modal ${className}`}>
        <div className="flex items-center justify-around h-16 px-2">
          {navigationItems?.map((item) => {
            const isActive = isActiveTab(item?.path);
            return (
              <button
                key={item?.path}
                onClick={() => handleTabClick(item)}
                className={`flex flex-col items-center justify-center min-w-0 flex-1 py-2 px-1 transition-micro hover-scale ${
                  isActive 
                    ? 'text-primary' :'text-text-secondary hover:text-text-primary'
                }`}
                title={item?.tooltip}
              >
                <div className="relative">
                  <Icon 
                    name={item?.icon} 
                    size={22} 
                    className={isActive ? 'text-primary' : 'text-current'} 
                  />
                  {item?.badge && item?.badge > 0 && (
                    <span className="absolute -top-2 -right-2 bg-error text-error-foreground text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {item?.badge > 99 ? '99+' : item?.badge}
                    </span>
                  )}
                </div>
                <span className={`text-xs mt-1 font-medium truncate ${
                  isActive ? 'text-primary' : 'text-current'
                }`}>
                  {item?.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
      {/* Desktop Sidebar Navigation */}
      <nav className={`hidden lg:flex fixed left-0 top-0 bottom-0 z-100 w-60 bg-surface border-r border-border shadow-card flex-col ${className}`}>
        {/* Logo Section */}
        <div className="flex items-center space-x-3 p-6 border-b border-border">
          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="Users" size={24} color="white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-text-primary">
              ContactSync Pro
            </h1>
          </div>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 py-6">
          <div className="space-y-2 px-4">
            {navigationItems?.map((item) => {
              const isActive = isActiveTab(item?.path);
              return (
                <button
                  key={item?.path}
                  onClick={() => handleTabClick(item)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-micro hover-lift ${
                    isActive 
                      ? 'bg-primary text-primary-foreground shadow-card' 
                      : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                  title={item?.tooltip}
                >
                  <div className="relative">
                    <Icon 
                      name={item?.icon} 
                      size={20} 
                      className="text-current" 
                    />
                    {item?.badge && item?.badge > 0 && (
                      <span className="absolute -top-2 -right-2 bg-error text-error-foreground text-xs rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
                        {item?.badge > 99 ? '99+' : item?.badge}
                      </span>
                    )}
                  </div>
                  <span className="font-medium">
                    {item?.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Settings Section */}
        <div className="p-4 border-t border-border">
          <Button
            variant="ghost"
            className="w-full justify-start"
            iconName="Settings"
            iconPosition="left"
            onClick={() => navigate('/settings')}
          >
            Settings
          </Button>
        </div>
      </nav>
    </>
  );
};

export default BottomTabNavigation;